import {
  createCapabilityChecker,
  targetAllows,
} from '@/features/authentication/lib/authorizationCapabilities';
import {
  useAvailableOperations,
  useTargetOperations,
} from '@/features/authentication/services/permissionQueries';
import type {
  AuthorizationOperation,
  CapabilityChecker,
  PermissionTargetRef,
} from '@/features/authentication/types/permissions';
import { useCallback, useMemo } from 'react';

/** Global true-only availability used for navigation and non-target entry points. */
export function useCapabilities(): {
  hasCapability: CapabilityChecker;
  isPending: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const available = useAvailableOperations();
  const response = available.isError ? undefined : available.data;
  const hasCapability = useMemo(() => createCapabilityChecker(response), [response]);
  return {
    hasCapability,
    isPending: available.isPending,
    isError: available.isError,
    refetch: () => {
      void available.refetch();
    },
  };
}

/** Exact Organization/Project Operations for the currently loaded page targets. */
export function useTargetCapabilities(targets: readonly PermissionTargetRef[]) {
  const result = useTargetOperations(targets);
  const canOrganization = useCallback(
    (operation: AuthorizationOperation, organizationId: string) =>
      targetAllows(result.targets, { type: 'organization', id: organizationId }, operation),
    [result.targets],
  );
  const canProject = useCallback(
    (operation: AuthorizationOperation, projectId: string) =>
      targetAllows(result.targets, { type: 'project', id: projectId }, operation),
    [result.targets],
  );
  return {
    canOrganization,
    canProject,
    isPending: result.isPending,
    isError: result.isError,
    refetch: result.refetch,
  };
}
